import { useState, useEffect, FC } from "react";
import { callErrorToast } from "../../../utilities/index.js";
import { AddressBookService as RegistryAddressBookService } from "../../../services/registry/addressBook.ts";
import { AddressBookService as LogisticAddressBookService } from "../../../services/logistic/addressBook.ts";
import { AddressBookService as ShipmentAddressBookService } from "../../../services/shipment/addressBook.ts";
import { ErrorResponse } from "../../../Models/Error.ts";
import { AddressBook } from "../../../Models/AddressBook.ts";
import {
  AddressBookDeleteContextValue,
  AddressBookDeleteProviderProps,
} from "./";
import { AddressBookDeleteContext } from "./";

/**
 * @const services
 * Centralized object to access different AddressBook service instances based on the service type.
 */
const services = {
  logistic: {
    addressBookService: new LogisticAddressBookService(),
  },
  registry: {
    addressBookService: new RegistryAddressBookService(),
  },
  shipment: {
    addressBookService: new ShipmentAddressBookService(),
  },
};

/**
 * @component AddressBookDeleteProvider
 * Provider component responsible for managing the state and logic for batch deletion of AddressBook entries.
 * It handles the API calls, loading state, error reporting, and success callback.
 * @param {AddressBookDeleteProviderProps} props - The component properties.
 * @returns React elements with the DeleteAddressBookContext provided.
 */
const AddressBookDeleteProvider: FC<AddressBookDeleteProviderProps> = ({
  children,
  data = [],
  callback,
  service = "registry",
}) => {
  // Access the correct service instance based on the prop
  const addressBookService = services[service].addressBookService;

  /**
   * @var error
   * State to hold any error response received during the API call.
   */
  const [error, setError] = useState<ErrorResponse<AddressBook> | null>(null);

  /**
   * @var loader
   * Boolean state indicating if the deletion process is currently loading.
   */
  const [loader, setLoader] = useState<boolean>(false);

  /**
   * @function deleteAddressBooks
   * Executes the deletion of all IDs in the 'data' array concurrently using Promise.all.
   * Handles success (calls callback) and failure (sets error state).
   */
  const deleteAddressBooks = async (): Promise<void> => {
    setLoader(true);

    try {
      const results = await Promise.allSettled(
        data.map((id) => addressBookService.delete(id))
      );

      const failed = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected"
      );
      const success = results.filter(
        (r): r is PromiseFulfilledResult<void> => r.status === "fulfilled"
      );

      if (failed.length > 0) {
        const firstError = failed[0].reason as ErrorResponse<AddressBook>;
        setError(firstError);

        if (success.length > 0 && callback) {
          callback();
        }

        return;
      }

      if (callback) callback();
    } catch (unexpected) {
      const err: ErrorResponse<AddressBook> = {
        response: {
          data: {},
        },
      };

      setError(err);
    } finally {
      setLoader(false);
    }
  };

  /**
   * @hook useEffect
   * Watches for changes in the 'error' state. If an error exists, it triggers a toast notification.
   * The error state is reset after showing the toast to allow the toast to be triggered again later.
   */
  useEffect(() => {
    if (error) {
      callErrorToast(error);
      // Optional: Reset error state after showing toast if you want to allow re-triggering the toast
      // without leaving the component (e.g., setError(null);)
    }
  }, [error]);

  /**
   * @var contextValue
   * The assembled value passed down to all consumers of the Context.
   */
  const contextValue: AddressBookDeleteContextValue = {
    loader,
    deleteAddressBooks,
    data,
    callback,
    service, // Include service in the context value
  };

  return (
    <AddressBookDeleteContext.Provider value={contextValue}>
      {children}
    </AddressBookDeleteContext.Provider>
  );
};

export { AddressBookDeleteProvider };
